import { Col, Row } from '@amzn/stencil-react-components/layout';
import { H2, Text } from '@amzn/stencil-react-components/text';
import { Chip, ChipSize } from '@amzn/stencil-react-components/chip';
import { Modal, ModalContent } from '@amzn/stencil-react-components/modal';

import { token } from '@amzn/stencil-design-tokens/js/web/utils';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, ButtonVariant } from '@amzn/stencil-react-components/button';

import { RichHtmlContent } from 'src/v2/shared/ui/RichHtmlContent';
import type { DetailField, DetailTone, InsightDetail } from '../logic/insightDetail';
import { accentBar } from './panelEdges';

/** Quotes what the audit asked. Red, because this view is only reached from a failure. */
const QUESTION_CALLOUT = accentBar('color.status.critical-border-default');

/**
 * Which colour each verdict takes.
 *
 * `color.status.*-icon` rather than a text token: Stencil ships no red or green under
 * `color.text`, and these are what the rest of `src/v2` already uses for status-coloured
 * text.
 */
const TONE_COLORS: Record<DetailTone, string> = {
  critical: 'color.status.critical-icon',
  positive: 'color.status.success-icon',
};

/**
 * Share of a field row taken by its label and by its value.
 *
 * Proportions rather than a width, because Stencil has no dimension token at column scale
 * and a raw one would not track the modal's own width.
 */
const LABEL_SHARE = 1;
const VALUE_SHARE = 2;

/**
 * Clearance for the close button, which the modal positions absolutely over the title.
 *
 * Stencil reserves the same amount for the title it renders itself. Replacing that title
 * to seat the read-only marker beside it means reserving the space here instead. Through
 * `css` because the layout props set padding on all four edges at once.
 */
const TITLE_ROW = { paddingRight: token('dimensions.spacing.400') } as const;

interface InsightDetailModalProps {
  /** The row's recorded facts. Absent when no row is open. */
  detail: InsightDetail | undefined;
  /** Marks the view as reporting recorded facts rather than offering an edit. */
  readOnlyLabel: string;
  /** Accessible label for the close control. */
  closeLabel: string;
  /** Label for the link out, shown only when the detail carries somewhere to go. */
  viewLabel: string;
  onClose: () => void;
}

/**
 * What one recurring failure or outstanding action has on record.
 *
 * Reports only: nothing here edits an audit or a task, which the read-only marker states
 * rather than leaving the reader to infer from the absence of controls.
 *
 * Renders nothing when no row is open, so the caller can hold one modal for the whole
 * list instead of one per row.
 */
export function InsightDetailModal({
  detail,
  readOnlyLabel,
  closeLabel,
  viewLabel,
  onClose,
}: InsightDetailModalProps): JSX.Element | null {
  const navigate = useNavigate();
  const viewHref = detail?.viewHref;
  const handleView = useCallback(() => {
    if (viewHref) {
      void navigate(viewHref);
    }
  }, [navigate, viewHref]);

  if (!detail) {
    return null;
  }

  return (
    <Modal isOpen close={onClose} aria-label={detail.title}>
      <ModalContent
        titleText={detail.title}
        closeButtonAltText={closeLabel}
        onCloseButtonClick={onClose}
        renderTitle={({ id, titleText }) => (
          <Row
            alignItems="center"
            justifyContent="space-between"
            gridGap="dimensions.spacing.200"
            flexWrap="wrap"
            css={TITLE_ROW}
          >
            {/* The id stays on the heading alone, so the close button is described by
                the question rather than by the marker beside it. */}
            <H2 id={id} margin="dimensions.spacing.none" fontWeight="bold">
              {titleText}
            </H2>
            <Chip size={ChipSize.ExtraSmall}>{readOnlyLabel}</Chip>
          </Row>
        )}
      >
        {/* No padding of its own: the modal already pads its body. */}
        <Col gridGap="dimensions.spacing.300" minWidth={0}>
          {detail.questionText ? (
            <Col
              backgroundColor="color.surface.bg-muted"
              padding="dimensions.spacing.300"
              css={QUESTION_CALLOUT}
            >
              {/* Authored as rich text in Helios, so it arrives as HTML and has to be
                  rendered rather than printed. Images have already been removed upstream,
                  in AuditInsightsTab, because this tab holds no media map to resolve them
                  with — so there is nothing here for a preview to open, and no error for
                  `onImageError` to recover from. */}
              <RichHtmlContent html={detail.questionText} enableImagePreview={false} />
            </Col>
          ) : null}
          {detail.provenance ? (
            <Text fontSize="T100" color="color.text.secondary">
              {detail.provenance}
            </Text>
          ) : null}
          <Col gridGap="dimensions.spacing.200" minWidth={0}>
            {detail.fields.map((field) => (
              <FieldRow key={field.label} field={field} />
            ))}
          </Col>
          {detail.viewHref ? (
            // Reporting ends here; acting on it happens on the record's own page.
            <Row justifyContent="flex-end">
              <Button variant={ButtonVariant.Secondary} onClick={handleView}>
                {viewLabel}
              </Button>
            </Row>
          ) : null}
        </Col>
      </ModalContent>
    </Modal>
  );
}

/** One recorded fact: its label, then the value beside it. */
function FieldRow({ field }: { field: DetailField }): JSX.Element {
  return (
    <Row gridGap="dimensions.spacing.200" alignItems="baseline" minWidth={0}>
      <Col flex={LABEL_SHARE} minWidth={0}>
        <Text fontSize="T200" color="color.text.secondary">
          {field.label}
        </Text>
      </Col>
      <Col flex={VALUE_SHARE} minWidth={0}>
        {/* A verdict is weighted and coloured; a plain fact is neither. */}
        <Text
          fontSize="T200"
          fontWeight={field.tone ? 'bold' : 'medium'}
          {...(field.tone ? { color: TONE_COLORS[field.tone] } : {})}
        >
          {field.value}
        </Text>
      </Col>
    </Row>
  );
}
